import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, numeric, index, uniqueIndex, date } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export * from "./models/auth";
export * from "./models/chat";
export * from "./models/reports";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

export const terrariumStyles = [
  { id: "forest", name: "Forest Oasis", price: 499, description: "Lush miniature forest with moss, ferns, and tiny plants" },
  { id: "desert", name: "DesertScape", price: 499, description: "Succulents, cacti, and beautiful sand formations" },
  { id: "tropical", name: "Tropical Paradise", price: 649, description: "Vibrant tropical plants in a geometric vessel" },
  { id: "deluxe", name: "Deluxe Custom", price: 999, description: "A larger, more elaborate creation tailored to your vision" },
] as const;

export type TerrariumStyle = typeof terrariumStyles[number];

export const experiencePackages = [
  { id: "2-person", name: "Duo Nature Experience", price: 2000 },
  { id: "4-person", name: "Group Nature Experience", price: 4000 },
] as const;

export type ExperiencePackage = typeof experiencePackages[number];

export const biomes = [
  { id: "desert", name: "Desert", description: "Arid landscapes featuring succulents, cacti, and drought-resistant plants with sandy terrain" },
  { id: "forest", name: "Forest", description: "Lush woodland ecosystems with moss, ferns, and shade-loving plants" },
] as const;

export type Biome = typeof biomes[number];

export const orderStatuses = ["pending", "approved", "building", "delivered"] as const;
export type OrderStatus = typeof orderStatuses[number];

export const shipmentCarriers = ["UPS", "FedEx", "local"] as const;
export type ShipmentCarrier = typeof shipmentCarriers[number];

export const shipmentStatuses = ["pending", "in_transit", "delivered", "returned"] as const;
export type ShipmentStatus = typeof shipmentStatuses[number];

export const subscriptionStatuses = ["active", "cancelled", "past_due"] as const;
export type SubscriptionStatus = typeof subscriptionStatuses[number];

export const tierTypes = ["base", "premium", "custom"] as const;
export type TierType = typeof tierTypes[number];

export const discountTypes = ["percentage", "fixed_amount", "free_shipping"] as const;
export type DiscountType = typeof discountTypes[number];

// CRM Enums
export const customerSources = ["referral", "social", "organic", "direct"] as const;
export type CustomerSource = typeof customerSources[number];

export const customerSegments = ["VIP", "regular", "new"] as const;
export type CustomerSegment = typeof customerSegments[number];

// Communication preferences type for JSONB column
export interface CommunicationPreferences {
  email: boolean;
  sms: boolean;
  marketing: boolean;
  orderUpdates: boolean;
  careReminders: boolean;
}

// ============================================================================
// CUSTOMERS TABLE
// ============================================================================

export const customers = pgTable("customers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  phone: text("phone"),
  name: text("name").notNull(),
  // Address fields
  address: text("address"),
  city: text("city"),
  state: text("state").notNull(),
  zipCode: text("zip_code"),
  country: text("country").default("USA"),
  // CRM fields
  birthDate: date("birth_date"), // For birthday promos
  customerSource: text("customer_source"), // referral, social, organic, direct
  referredBy: varchar("referred_by").references(() => customers.id), // Self-referential FK
  notes: text("notes"), // Internal CRM notes
  // Aggregated metrics (updated on order completion)
  lifetimeValue: integer("lifetime_value").default(0), // Total spent in cents
  totalOrders: integer("total_orders").default(0),
  lastOrderDate: timestamp("last_order_date"),
  // Preferences and segmentation
  communicationPreferences: jsonb("communication_preferences").$type<CommunicationPreferences>(),
  segment: text("segment").default("new"), // VIP, regular, new
  tags: text("tags").array(), // Flexible tagging for marketing
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Zod schema for communication preferences
export const communicationPreferencesSchema = z.object({
  email: z.boolean().default(true),
  sms: z.boolean().default(false),
  marketing: z.boolean().default(true),
  orderUpdates: z.boolean().default(true),
  careReminders: z.boolean().default(true),
});

export const insertCustomerSchema = createInsertSchema(customers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lifetimeValue: true, // Calculated field
  totalOrders: true, // Calculated field
  lastOrderDate: true, // Calculated field
}).extend({
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  name: z.string().min(2, "Name must be at least 2 characters"),
  // Address fields
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().min(2, "Please select your state"),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format").optional().or(z.literal("")),
  country: z.string().default("USA"),
  // CRM fields
  birthDate: z.string().optional(), // ISO date string for birthday
  customerSource: z.enum(customerSources).optional(),
  referredBy: z.string().uuid("Invalid referrer ID").optional(),
  notes: z.string().optional(),
  // Preferences and segmentation
  communicationPreferences: communicationPreferencesSchema.optional(),
  segment: z.enum(customerSegments).default("new"),
  tags: z.array(z.string()).optional(),
});

// Schema for updating customer CRM metrics (internal use)
export const updateCustomerMetricsSchema = z.object({
  lifetimeValue: z.number().int().nonnegative(),
  totalOrders: z.number().int().nonnegative(),
  lastOrderDate: z.date(),
});

export type Customer = typeof customers.$inferSelect;
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;
export type UpdateCustomerMetrics = z.infer<typeof updateCustomerMetricsSchema>;

// ============================================================================
// ORDERS TABLE
// ============================================================================

// Gift wrapping types
export const giftWrappingTypes = ["standard", "premium", "none"] as const;
export type GiftWrappingType = typeof giftWrappingTypes[number];

export const orders = pgTable("orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id),
  terrariumStyle: text("terrarium_style").notNull(),
  biome: text("biome").notNull(),
  tier: text("tier").notNull().default("base"),
  price: integer("price").notNull(),
  deliveryWeek: text("delivery_week").notNull(),
  status: text("status").notNull().default("pending"),
  // Gift order fields - extended
  isGift: boolean("is_gift").default(false),
  giftRecipientName: text("gift_recipient_name"),
  giftRecipientEmail: text("gift_recipient_email"),
  giftOccasion: text("gift_occasion"),
  giftMessage: text("gift_message"),
  // Gift delivery address (if different from billing)
  useDifferentDeliveryAddress: boolean("use_different_delivery_address").default(false),
  giftDeliveryAddress: text("gift_delivery_address"),
  giftDeliveryCity: text("gift_delivery_city"),
  giftDeliveryState: text("gift_delivery_state"),
  giftDeliveryZip: text("gift_delivery_zip"),
  // Preferred delivery date for gifts
  preferredDeliveryDate: text("preferred_delivery_date"),
  // Notification preferences (false = keep it a surprise)
  notifyOnShip: boolean("notify_on_ship").default(true),
  // Gift wrapping preference
  giftWrapping: text("gift_wrapping").default("standard"),
  // Hide pricing on gift receipt
  hideGiftReceipt: boolean("hide_gift_receipt").default(true),
  // Other fields
  specialRequests: text("special_requests"),
  insuranceOptIn: boolean("insurance_opt_in").default(true),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertOrderSchema = createInsertSchema(orders).omit({
  id: true,
  createdAt: true,
}).extend({
  customerId: z.string().uuid("Invalid customer ID"),
  terrariumStyle: z.string().min(1, "Please select a terrarium style"),
  biome: z.string().min(1, "Please select a biome"),
  tier: z.enum(tierTypes).default("base"),
  price: z.number().positive("Price must be positive"),
  deliveryWeek: z.string().min(1, "Please select a delivery week"),
  status: z.enum(orderStatuses).default("pending"),
  // Gift fields - extended
  isGift: z.boolean().optional().default(false),
  giftRecipientName: z.string().optional(),
  giftRecipientEmail: z.string().email().optional().or(z.literal("")),
  giftOccasion: z.string().optional(),
  giftMessage: z.string().max(200, "Gift message must be 200 characters or less").optional(),
  useDifferentDeliveryAddress: z.boolean().optional().default(false),
  giftDeliveryAddress: z.string().optional(),
  giftDeliveryCity: z.string().optional(),
  giftDeliveryState: z.string().optional(),
  giftDeliveryZip: z.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format").optional().or(z.literal("")),
  preferredDeliveryDate: z.string().optional(),
  notifyOnShip: z.boolean().optional().default(true),
  giftWrapping: z.enum(giftWrappingTypes).optional().default("standard"),
  hideGiftReceipt: z.boolean().optional().default(true),
  // Other fields
  specialRequests: z.string().optional(),
  insuranceOptIn: z.boolean().optional().default(true),
  stripePaymentIntentId: z.string().optional(),
  stripeSubscriptionId: z.string().optional(),
});

export type Order = typeof orders.$inferSelect;
export type InsertOrder = z.infer<typeof insertOrderSchema>;

// ============================================================================
// ORDER COSTS TABLE (Materials cost tracking for profit margin calculation)
// ============================================================================

export const costCategories = ["plants", "container", "soil", "decorative", "packaging"] as const;
export type CostCategory = typeof costCategories[number];

export const orderCosts = pgTable("order_costs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id").notNull().references(() => orders.id),
  category: text("category").notNull(), // plants, container, soil, decorative, packaging
  description: text("description").notNull(),
  amount: integer("amount").notNull(), // Cost in cents
  quantity: integer("quantity").notNull().default(1),
  vendor: text("vendor"),
  purchaseDate: timestamp("purchase_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("order_costs_order_id_idx").on(table.orderId),
]);

export const insertOrderCostSchema = createInsertSchema(orderCosts).omit({
  id: true,
  createdAt: true,
}).extend({
  orderId: z.string().uuid("Invalid order ID"),
  category: z.enum(costCategories, { errorMap: () => ({ message: "Invalid cost category" }) }),
  description: z.string().min(1, "Description is required"),
  amount: z.number().int().positive("Amount must be a positive integer (in cents)"),
  quantity: z.number().int().positive("Quantity must be a positive integer").default(1),
  vendor: z.string().optional(),
  purchaseDate: z.date().optional(),
});

export type OrderCost = typeof orderCosts.$inferSelect;
export type InsertOrderCost = z.infer<typeof insertOrderCostSchema>;

// ============================================================================
// TIME ENTRIES TABLE (Build hour tracking for $90/hr target)
// ============================================================================

export const timeEntryTaskTypes = ["design", "building", "finishing", "cleanup"] as const;
export type TimeEntryTaskType = typeof timeEntryTaskTypes[number];

export const timeEntries = pgTable("time_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id").notNull().references(() => orders.id),
  hours: numeric("hours", { precision: 5, scale: 2 }).notNull(), // e.g., 2.50 hours
  minutes: integer("minutes").notNull().default(0), // Alternative: track in minutes
  taskType: text("task_type").notNull(), // design, building, finishing, cleanup
  notes: text("notes"),
  date: date("date", { mode: "date" }).notNull(), // The work date
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("time_entries_order_id_idx").on(table.orderId),
  index("time_entries_date_idx").on(table.date),
  index("time_entries_task_type_idx").on(table.taskType),
]);

export const insertTimeEntrySchema = createInsertSchema(timeEntries).omit({
  id: true,
  createdAt: true,
}).extend({
  orderId: z.string().uuid("Invalid order ID"),
  hours: z.string().regex(/^\d+(\.\d{1,2})?$/, "Hours must be a valid decimal (e.g., 2.50)"),
  minutes: z.number().int().min(0).max(59).default(0),
  taskType: z.enum(timeEntryTaskTypes, { errorMap: () => ({ message: "Invalid task type" }) }),
  notes: z.string().optional(),
  date: z.date({ required_error: "Date is required" }),
});

export type TimeEntry = typeof timeEntries.$inferSelect;
export type InsertTimeEntry = z.infer<typeof insertTimeEntrySchema>;

// ============================================================================
// ORDER STATUS HISTORY TABLE - Complete audit trail for order status changes
// ============================================================================

export const orderStatusHistory = pgTable("order_status_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id").notNull().references(() => orders.id),
  previousStatus: text("previous_status"),
  newStatus: text("new_status").notNull(),
  changedBy: text("changed_by").notNull(), // userId or 'system'
  notes: text("notes"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("order_status_history_order_id_idx").on(table.orderId),
]);

export const insertOrderStatusHistorySchema = createInsertSchema(orderStatusHistory).omit({
  id: true,
  createdAt: true,
}).extend({
  orderId: z.string().uuid("Invalid order ID"),
  previousStatus: z.enum(orderStatuses).nullable().optional(),
  newStatus: z.enum(orderStatuses),
  changedBy: z.string().min(1, "Changed by is required"),
  notes: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type OrderStatusHistory = typeof orderStatusHistory.$inferSelect;
export type InsertOrderStatusHistory = z.infer<typeof insertOrderStatusHistorySchema>;

// ============================================================================
// SUBSCRIPTIONS TABLE
// ============================================================================

export const subscriptions = pgTable("subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id),
  orderId: varchar("order_id").notNull().references(() => orders.id),
  stripeSubscriptionId: text("stripe_subscription_id").notNull(),
  status: text("status").notNull().default("active"),
  monthlyAmount: integer("monthly_amount").notNull().default(2000), // 2000 cents = $20
  currentPeriodEnd: timestamp("current_period_end"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertSubscriptionSchema = createInsertSchema(subscriptions).omit({
  id: true,
  createdAt: true,
}).extend({
  customerId: z.string().uuid("Invalid customer ID"),
  orderId: z.string().uuid("Invalid order ID"),
  stripeSubscriptionId: z.string().min(1, "Stripe subscription ID is required"),
  status: z.enum(subscriptionStatuses).default("active"),
  monthlyAmount: z.number().positive("Monthly amount must be positive").default(2000),
  currentPeriodEnd: z.date().optional(),
});

export type Subscription = typeof subscriptions.$inferSelect;
export type InsertSubscription = z.infer<typeof insertSubscriptionSchema>;

// ============================================================================
// SHIPMENTS TABLE
// Tracks delivery of orders within 90min radius of Denver
// ============================================================================

export const shipments = pgTable("shipments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id").notNull().references(() => orders.id),
  carrier: text("carrier").notNull(), // UPS, FedEx, local
  trackingNumber: text("tracking_number"),
  trackingUrl: text("tracking_url"),
  shippingMethod: text("shipping_method"),
  shippingCost: integer("shipping_cost"), // in cents
  weight: numeric("weight", { precision: 10, scale: 2 }), // in pounds
  dimensions: jsonb("dimensions"), // { length, width, height, unit }
  shippedAt: timestamp("shipped_at"),
  estimatedDelivery: timestamp("estimated_delivery"),
  deliveredAt: timestamp("delivered_at"),
  status: text("status").notNull().default("pending"), // pending, in_transit, delivered, returned
  signedBy: text("signed_by"),
  deliveryNotes: text("delivery_notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("shipments_order_id_idx").on(table.orderId),
  index("shipments_status_idx").on(table.status),
]);

// Dimensions type for shipments
export const dimensionsSchema = z.object({
  length: z.number().positive().optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  unit: z.enum(["in", "cm"]).default("in"),
});

export type Dimensions = z.infer<typeof dimensionsSchema>;

export const insertShipmentSchema = createInsertSchema(shipments).omit({
  id: true,
  createdAt: true,
}).extend({
  orderId: z.string().uuid("Invalid order ID"),
  carrier: z.enum(shipmentCarriers),
  trackingNumber: z.string().optional(),
  trackingUrl: z.string().url().optional().or(z.literal("")),
  shippingMethod: z.string().optional(),
  shippingCost: z.number().int().nonnegative("Shipping cost must be non-negative").optional(),
  weight: z.number().positive("Weight must be positive").optional(),
  dimensions: dimensionsSchema.optional(),
  shippedAt: z.date().optional(),
  estimatedDelivery: z.date().optional(),
  deliveredAt: z.date().optional(),
  status: z.enum(shipmentStatuses).default("pending"),
  signedBy: z.string().optional(),
  deliveryNotes: z.string().optional(),
});

export type Shipment = typeof shipments.$inferSelect;
export type InsertShipment = z.infer<typeof insertShipmentSchema>;

// ============================================================================
// WAITLIST TABLE
// ============================================================================

export const waitlist = pgTable("waitlist", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  phone: text("phone"),
  name: text("name"),
  state: text("state"),
  biomePreference: text("biome_preference"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertWaitlistSchema = createInsertSchema(waitlist).omit({
  id: true,
  createdAt: true,
}).extend({
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  name: z.string().optional(),
  state: z.string().optional(),
  biomePreference: z.string().optional(),
});

export type WaitlistEntry = typeof waitlist.$inferSelect;
export type InsertWaitlist = z.infer<typeof insertWaitlistSchema>;

// Legacy waitlist schema for backwards compatibility
export const waitlistSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  state: z.string().min(2, "Please enter your state"),
});

// ============================================================================
// EXPERIENCES TABLE
// ============================================================================

export const experiences = pgTable("experiences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id),
  packageType: text("package_type").notNull(),
  price: integer("price").notNull(),
  preferredDate: timestamp("preferred_date"),
  status: text("status").notNull().default("pending"),
  specialRequests: text("special_requests"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertExperienceSchema = createInsertSchema(experiences).omit({
  id: true,
  createdAt: true,
}).extend({
  customerId: z.string().uuid("Invalid customer ID"),
  packageType: z.enum(["2-person", "4-person"]),
  price: z.number().positive("Price must be positive"),
  preferredDate: z.date().optional(),
  status: z.string().default("pending"),
  specialRequests: z.string().optional(),
});

export type Experience = typeof experiences.$inferSelect;
export type InsertExperience = z.infer<typeof insertExperienceSchema>;

// ============================================================================
// BLOCKED WEEKS TABLE
// ============================================================================

export const blockedWeeks = pgTable("blocked_weeks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  weekStart: text("week_start").notNull().unique(),
});

export const insertBlockedWeekSchema = createInsertSchema(blockedWeeks).omit({
  id: true,
}).extend({
  weekStart: z.string().min(1, "Week start date is required"),
});

export type BlockedWeek = typeof blockedWeeks.$inferSelect;
export type InsertBlockedWeek = z.infer<typeof insertBlockedWeekSchema>;

// ============================================================================
// CARE REMINDERS TABLE
// Supports both legacy pre-orders (preOrderId) and new orders (orderId/customerId)
// ============================================================================

export const careReminders = pgTable("care_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Legacy support - references pre_orders table
  preOrderId: varchar("pre_order_id"),
  // New model - references orders and customers tables
  orderId: varchar("order_id"),
  customerId: varchar("customer_id"),
  scheduledFor: timestamp("scheduled_for").notNull(),
  sentAt: timestamp("sent_at"),
  reminderNumber: integer("reminder_number").notNull(),
  reminderType: text("reminder_type").notNull().default("care"),
  status: text("status").notNull().default("pending"),
  sent: boolean("sent").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCareReminderSchema = createInsertSchema(careReminders).omit({
  id: true,
  createdAt: true,
}).extend({
  preOrderId: z.string().optional(),
  orderId: z.string().optional(),
  customerId: z.string().optional(),
  scheduledFor: z.date(),
  sentAt: z.date().optional(),
  reminderNumber: z.number().int().positive("Reminder number must be positive"),
  reminderType: z.string().default("care"),
  status: z.string().default("pending"),
  sent: z.boolean().default(false),
});

export type CareReminder = typeof careReminders.$inferSelect;
export type InsertCareReminder = z.infer<typeof insertCareReminderSchema>;

// ============================================================================
// LEGACY PRE-ORDERS TABLE (kept for backwards compatibility)
// ============================================================================

export const preOrders = pgTable("pre_orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  state: text("state").notNull().default("CO"),
  terrariumStyle: text("terrarium_style").notNull(),
  deliveryWeek: text("delivery_week").notNull(),
  specialRequests: text("special_requests"),
  giftMessage: text("gift_message"),
  giftRecipientName: text("gift_recipient_name"),
  giftRecipientEmail: text("gift_recipient_email"),
  isGift: boolean("is_gift").default(false),
  wantsInsurance: boolean("wants_insurance").default(true),
  interestedInLearning: boolean("interested_in_learning").default(false),
  status: text("status").default("pending"),
  stripePaymentId: text("stripe_payment_id"),
  totalAmount: integer("total_amount"),
  portalToken: text("portal_token").unique(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPreOrderSchema = createInsertSchema(preOrders).omit({
  id: true,
  createdAt: true,
  stripePaymentId: true,
  status: true,
  portalToken: true,
}).extend({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  state: z.string().min(2, "Please select your state"),
  terrariumStyle: z.string().min(1, "Please select a terrarium style"),
  deliveryWeek: z.string().min(1, "Please select a delivery week"),
  specialRequests: z.string().optional(),
  giftMessage: z.string().optional(),
  giftRecipientName: z.string().optional(),
  giftRecipientEmail: z.string().email().optional().or(z.literal("")),
  isGift: z.boolean().optional(),
  wantsInsurance: z.boolean().optional(),
  interestedInLearning: z.boolean().optional(),
  totalAmount: z.number().optional(),
});

export type InsertPreOrder = z.infer<typeof insertPreOrderSchema>;
export type PreOrder = typeof preOrders.$inferSelect;

// ============================================================================
// LEGACY BILLING & MAINTENANCE TABLES (kept for backwards compatibility)
// ============================================================================

export const legacySubscriptions = pgTable("legacy_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  preOrderId: varchar("pre_order_id").notNull().references(() => preOrders.id),
  status: text("status").default("active"),
  monthlyAmount: integer("monthly_amount").default(20),
  nextBillingDate: timestamp("next_billing_date"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  createdAt: timestamp("created_at").defaultNow(),
  cancelledAt: timestamp("cancelled_at"),
});

export const billingHistory = pgTable("billing_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subscriptionId: varchar("subscription_id").notNull(),
  amount: integer("amount").notNull(),
  status: text("status").default("paid"),
  billingDate: timestamp("billing_date").defaultNow(),
  description: text("description"),
});

export const maintenanceRequests = pgTable("maintenance_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  preOrderId: varchar("pre_order_id").notNull().references(() => preOrders.id),
  status: text("status").default("pending"),
  notes: text("notes"),
  requestedAt: timestamp("requested_at").defaultNow(),
  scheduledFor: timestamp("scheduled_for"),
});

export type LegacySubscription = typeof legacySubscriptions.$inferSelect;
export type InsertLegacySubscription = typeof legacySubscriptions.$inferInsert;
export type BillingHistoryEntry = typeof billingHistory.$inferSelect;
export type InsertBillingHistory = typeof billingHistory.$inferInsert;
export type MaintenanceRequest = typeof maintenanceRequests.$inferSelect;
export type InsertMaintenanceRequest = typeof maintenanceRequests.$inferInsert;

// Portal data type for API responses
export interface PortalData {
  order: PreOrder;
  subscription: LegacySubscription | null;
  billingHistory: BillingHistoryEntry[];
  maintenanceRequests: MaintenanceRequest[];
}

// ============================================================================
// WORKSHOP BOOKINGS TABLE
// Full-day terrarium-building experience workshops (9am-5pm)
// Pricing: $2000 (2-person) or $4000 (4-person)
// ============================================================================

export const workshopPackageTypes = ["2-person", "4-person"] as const;
export type WorkshopPackageType = typeof workshopPackageTypes[number];

export const workshopStatuses = ["pending", "confirmed", "completed", "cancelled"] as const;
export type WorkshopStatus = typeof workshopStatuses[number];

export const workshopBookings = pgTable("workshop_bookings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").references(() => customers.id),
  // Contact info (if no customer account)
  customerName: text("customer_name").notNull(),
  customerEmail: text("customer_email").notNull(),
  customerPhone: text("customer_phone"),
  // Workshop details
  workshopDate: date("workshop_date", { mode: "date" }).notNull(),
  packageType: text("package_type").notNull(), // "2-person" or "4-person"
  price: integer("price").notNull(), // 2000 for 2-person, 4000 for 4-person (in dollars)
  status: text("status").notNull().default("pending"),
  // Additional info
  specialRequests: text("special_requests"),
  notes: text("notes"),
  // Payment
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  stripeSessionId: text("stripe_session_id"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  confirmedAt: timestamp("confirmed_at"),
  cancelledAt: timestamp("cancelled_at"),
}, (table) => [
  index("workshop_bookings_date_idx").on(table.workshopDate),
  index("workshop_bookings_status_idx").on(table.status),
  index("workshop_bookings_customer_idx").on(table.customerId),
]);

export const insertWorkshopBookingSchema = createInsertSchema(workshopBookings).omit({
  id: true,
  createdAt: true,
  confirmedAt: true,
  cancelledAt: true,
}).extend({
  customerName: z.string().min(2, "Name must be at least 2 characters"),
  customerEmail: z.string().email("Please enter a valid email address"),
  customerPhone: z.string().optional(),
  workshopDate: z.date({ required_error: "Workshop date is required" }),
  packageType: z.enum(workshopPackageTypes, { errorMap: () => ({ message: "Please select a package type" }) }),
  price: z.number().positive("Price must be positive"),
  status: z.enum(workshopStatuses).default("pending"),
  specialRequests: z.string().optional(),
  notes: z.string().optional(),
  stripePaymentIntentId: z.string().optional(),
  stripeSessionId: z.string().optional(),
  customerId: z.string().uuid("Invalid customer ID").optional(),
});

export type WorkshopBooking = typeof workshopBookings.$inferSelect;
export type InsertWorkshopBooking = z.infer<typeof insertWorkshopBookingSchema>;

// ============================================================================
// WORKSHOP BLOCKED DATES TABLE
// Buffer days before/after workshops for Ryan's preparation
// ============================================================================

export const workshopBlockedDates = pgTable("workshop_blocked_dates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  blockedDate: date("blocked_date", { mode: "date" }).notNull().unique(),
  reason: text("reason"), // "buffer_before", "buffer_after", "manual", "holiday"
  relatedBookingId: varchar("related_booking_id").references(() => workshopBookings.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("workshop_blocked_dates_date_idx").on(table.blockedDate),
]);

export const insertWorkshopBlockedDateSchema = createInsertSchema(workshopBlockedDates).omit({
  id: true,
  createdAt: true,
}).extend({
  blockedDate: z.date({ required_error: "Blocked date is required" }),
  reason: z.string().optional(),
  relatedBookingId: z.string().uuid("Invalid booking ID").optional(),
});

export type WorkshopBlockedDate = typeof workshopBlockedDates.$inferSelect;
export type InsertWorkshopBlockedDate = z.infer<typeof insertWorkshopBlockedDateSchema>;

// Workshop pricing constants
export const WORKSHOP_PRICING = {
  "2-person": 2000,
  "4-person": 4000,
} as const;

// Workshop availability interface for API responses
export interface WorkshopAvailability {
  date: string; // ISO date string
  isAvailable: boolean;
  isWeekend: boolean;
  reason?: string; // "booked", "buffer", "blocked", "past", "weekday"
}

// ============================================================================
// CUSTOMER SHOWCASE GALLERY TABLES (Wave 24)
// Customer-submitted terrarium photos with moderation and voting system
// ============================================================================

export const showcaseTerrariumTypes = ["tropical", "desert", "moss", "forest", "custom"] as const;
export type ShowcaseTerrariumType = typeof showcaseTerrariumTypes[number];

export const showcaseSizes = ["small", "medium", "large", "extra-large"] as const;
export type ShowcaseSize = typeof showcaseSizes[number];

export const showcaseStatuses = ["pending", "approved", "featured", "rejected"] as const;
export type ShowcaseStatus = typeof showcaseStatuses[number];

// Customer Showcase Entries Table
export const showcaseEntries = pgTable("showcase_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Customer info (anonymized for display)
  customerFirstName: text("customer_first_name").notNull(),
  customerLocation: text("customer_location").notNull(), // City, State
  customerEmail: text("customer_email").notNull(), // For notifications, not displayed
  // Photo
  photoUrl: text("photo_url").notNull(),
  photoOriginalName: text("photo_original_name"),
  photoMimeType: text("photo_mime_type"),
  // Story and details
  story: text("story").notNull(),
  terrariumType: text("terrarium_type").notNull(),
  terrariumSize: text("terrarium_size").notNull(),
  productsUsed: text("products_used").array(), // Product names
  productIds: text("product_ids").array(), // Optional product IDs for linking
  // Voting
  votes: integer("votes").notNull().default(0),
  // Status and featuring
  status: text("status").notNull().default("pending"),
  isFeatured: boolean("is_featured").default(false),
  isSpotlight: boolean("is_spotlight").default(false), // Terrarium of the Month
  spotlightMonth: text("spotlight_month"), // e.g., "2024-01" for January 2024
  // Moderation
  moderatedBy: text("moderated_by"),
  moderatedAt: timestamp("moderated_at"),
  rejectionReason: text("rejection_reason"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  approvedAt: timestamp("approved_at"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("showcase_entries_status_idx").on(table.status),
  index("showcase_entries_type_idx").on(table.terrariumType),
  index("showcase_entries_featured_idx").on(table.isFeatured),
  index("showcase_entries_spotlight_idx").on(table.isSpotlight),
  index("showcase_entries_votes_idx").on(table.votes),
  index("showcase_entries_created_idx").on(table.createdAt),
]);

export const insertShowcaseEntrySchema = createInsertSchema(showcaseEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  approvedAt: true,
  moderatedBy: true,
  moderatedAt: true,
  votes: true,
}).extend({
  customerFirstName: z.string().min(1, "First name is required").max(50, "First name too long"),
  customerLocation: z.string().min(1, "Location is required").max(100, "Location too long"),
  customerEmail: z.string().email("Valid email is required"),
  photoUrl: z.string().url("Valid photo URL is required"),
  photoOriginalName: z.string().optional(),
  photoMimeType: z.string().optional(),
  story: z.string().min(10, "Story must be at least 10 characters").max(500, "Story must be 500 characters or less"),
  terrariumType: z.enum(showcaseTerrariumTypes, { errorMap: () => ({ message: "Please select a valid terrarium type" }) }),
  terrariumSize: z.enum(showcaseSizes, { errorMap: () => ({ message: "Please select a valid size" }) }),
  productsUsed: z.array(z.string()).optional(),
  productIds: z.array(z.string()).optional(),
  status: z.enum(showcaseStatuses).default("pending"),
  isFeatured: z.boolean().default(false),
  isSpotlight: z.boolean().default(false),
  spotlightMonth: z.string().optional(),
  rejectionReason: z.string().optional(),
});

export type ShowcaseEntry = typeof showcaseEntries.$inferSelect;
export type InsertShowcaseEntry = z.infer<typeof insertShowcaseEntrySchema>;

// Showcase Votes Table - Track individual votes to prevent duplicates
export const showcaseVotes = pgTable("showcase_votes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  entryId: varchar("entry_id").notNull().references(() => showcaseEntries.id, { onDelete: "cascade" }),
  visitorId: text("visitor_id").notNull(), // Anonymous visitor identifier (cookie/fingerprint)
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  votedAt: timestamp("voted_at").defaultNow().notNull(),
}, (table) => [
  index("showcase_votes_entry_idx").on(table.entryId),
  uniqueIndex("showcase_votes_unique_idx").on(table.entryId, table.visitorId),
]);

export const insertShowcaseVoteSchema = createInsertSchema(showcaseVotes).omit({
  id: true,
  votedAt: true,
}).extend({
  entryId: z.string().uuid("Invalid entry ID"),
  visitorId: z.string().min(1, "Visitor ID is required"),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
});

export type ShowcaseVote = typeof showcaseVotes.$inferSelect;
export type InsertShowcaseVote = z.infer<typeof insertShowcaseVoteSchema>;

// Showcase Gallery API response types
export interface ShowcaseEntryWithVoteStatus extends ShowcaseEntry {
  hasVoted: boolean;
}

export interface ShowcaseGalleryFilters {
  terrariumType?: ShowcaseTerrariumType | "all";
  size?: ShowcaseSize | "all";
  sortBy?: "recent" | "popular" | "featured";
  page?: number;
  limit?: number;
}

export interface ShowcaseGalleryResponse {
  entries: ShowcaseEntryWithVoteStatus[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  spotlightEntry?: ShowcaseEntry;
}

// ============================================================================
// BLOG SYSTEM TABLES
// Comprehensive blog infrastructure for terrarium content marketing
// ============================================================================

export const blogCategories = ["succulent-care", "moss-terrariums", "diy-projects", "seasonal-inspiration", "plant-guides", "terrarium-tips"] as const;
export type BlogCategory = typeof blogCategories[number];

export const blogStatuses = ["draft", "scheduled", "published", "archived"] as const;
export type BlogStatus = typeof blogStatuses[number];

// Blog Authors Table
export const blogAuthors = pgTable("blog_authors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  email: text("email").notNull(),
  bio: text("bio"),
  avatarUrl: text("avatar_url"),
  website: text("website"),
  twitter: text("twitter"),
  instagram: text("instagram"),
  linkedin: text("linkedin"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("blog_authors_slug_idx").on(table.slug),
]);

export const insertBlogAuthorSchema = createInsertSchema(blogAuthors).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  name: z.string().min(2, "Name must be at least 2 characters"),
  slug: z.string().min(2, "Slug must be at least 2 characters").regex(/^[a-z0-9-]+$/, "Slug must be lowercase with hyphens only"),
  email: z.string().email("Please enter a valid email address"),
  bio: z.string().optional(),
  avatarUrl: z.string().url().optional().or(z.literal("")),
  website: z.string().url().optional().or(z.literal("")),
  twitter: z.string().optional(),
  instagram: z.string().optional(),
  linkedin: z.string().optional(),
  isActive: z.boolean().default(true),
});

export type BlogAuthor = typeof blogAuthors.$inferSelect;
export type InsertBlogAuthor = z.infer<typeof insertBlogAuthorSchema>;

// Blog Posts Table
export const blogPosts = pgTable("blog_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  excerpt: text("excerpt"), // Short summary for cards/previews
  content: text("content").notNull(), // MDX/Markdown content
  authorId: varchar("author_id").notNull().references(() => blogAuthors.id),
  category: text("category").notNull(),
  tags: text("tags").array(), // Flexible tagging for filtering
  // Featured image
  featuredImage: text("featured_image"),
  featuredImageAlt: text("featured_image_alt"),
  // SEO fields
  metaTitle: text("meta_title"),
  metaDescription: text("meta_description"),
  canonicalUrl: text("canonical_url"),
  ogTitle: text("og_title"),
  ogDescription: text("og_description"),
  ogImage: text("og_image"),
  // Publishing
  status: text("status").notNull().default("draft"),
  publishedAt: timestamp("published_at"),
  scheduledFor: timestamp("scheduled_for"), // For content calendar scheduling
  // Metrics
  readingTimeMinutes: integer("reading_time_minutes"),
  viewCount: integer("view_count").default(0),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("blog_posts_slug_idx").on(table.slug),
  index("blog_posts_status_idx").on(table.status),
  index("blog_posts_category_idx").on(table.category),
  index("blog_posts_author_idx").on(table.authorId),
  index("blog_posts_published_at_idx").on(table.publishedAt),
]);

export const insertBlogPostSchema = createInsertSchema(blogPosts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  viewCount: true,
}).extend({
  title: z.string().min(5, "Title must be at least 5 characters"),
  slug: z.string().min(5, "Slug must be at least 5 characters").regex(/^[a-z0-9-]+$/, "Slug must be lowercase with hyphens only"),
  excerpt: z.string().max(300, "Excerpt must be 300 characters or less").optional(),
  content: z.string().min(100, "Content must be at least 100 characters"),
  authorId: z.string().uuid("Invalid author ID"),
  category: z.enum(blogCategories, { errorMap: () => ({ message: "Please select a valid category" }) }),
  tags: z.array(z.string()).optional(),
  featuredImage: z.string().url().optional().or(z.literal("")),
  featuredImageAlt: z.string().optional(),
  metaTitle: z.string().max(70, "Meta title should be 70 characters or less").optional(),
  metaDescription: z.string().max(160, "Meta description should be 160 characters or less").optional(),
  canonicalUrl: z.string().url().optional().or(z.literal("")),
  ogTitle: z.string().optional(),
  ogDescription: z.string().optional(),
  ogImage: z.string().url().optional().or(z.literal("")),
  status: z.enum(blogStatuses).default("draft"),
  publishedAt: z.date().optional(),
  scheduledFor: z.date().optional(),
  readingTimeMinutes: z.number().int().positive().optional(),
});

export type BlogPost = typeof blogPosts.$inferSelect;
export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;

// Related Posts Junction Table
export const relatedPosts = pgTable("related_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  postId: varchar("post_id").notNull().references(() => blogPosts.id, { onDelete: "cascade" }),
  relatedPostId: varchar("related_post_id").notNull().references(() => blogPosts.id, { onDelete: "cascade" }),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("related_posts_post_id_idx").on(table.postId),
  uniqueIndex("related_posts_unique_idx").on(table.postId, table.relatedPostId),
]);

export const insertRelatedPostSchema = createInsertSchema(relatedPosts).omit({
  id: true,
  createdAt: true,
}).extend({
  postId: z.string().uuid("Invalid post ID"),
  relatedPostId: z.string().uuid("Invalid related post ID"),
  sortOrder: z.number().int().default(0),
});

export type RelatedPost = typeof relatedPosts.$inferSelect;
export type InsertRelatedPost = z.infer<typeof insertRelatedPostSchema>;

// Blog category display names mapping
export const blogCategoryDisplayNames: Record<BlogCategory, string> = {
  "succulent-care": "Succulent Care",
  "moss-terrariums": "Moss Terrariums",
  "diy-projects": "DIY Projects",
  "seasonal-inspiration": "Seasonal Inspiration",
  "plant-guides": "Plant Guides",
  "terrarium-tips": "Terrarium Tips",
};

// Blog post with author joined
export interface BlogPostWithAuthor extends BlogPost {
  author: BlogAuthor;
}

// Blog post with author and related posts
export interface BlogPostFull extends BlogPostWithAuthor {
  relatedPosts: BlogPostWithAuthor[];
}

// ============================================================================
// PLANT INVENTORY TRACKING SYSTEM (Wave 25)
// Comprehensive live plant inventory management for terrarium business
// ============================================================================

// Plant Species - Master catalog of plant types
export const lightRequirements = ["low", "medium", "bright", "direct"] as const;
export type LightRequirement = typeof lightRequirements[number];

export const humidityLevels = ["low", "moderate", "high", "very_high"] as const;
export type HumidityLevel = typeof humidityLevels[number];

export const growthRates = ["slow", "moderate", "fast"] as const;
export type GrowthRate = typeof growthRates[number];

export const propagationMethods = ["division", "cutting", "spore", "seed", "offset", "layering"] as const;
export type PropagationMethod = typeof propagationMethods[number];

export const plantSpecies = pgTable("plant_species", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Naming
  scientificName: text("scientific_name").notNull().unique(),
  commonName: text("common_name").notNull(),
  family: text("family"),
  // Care requirements
  lightRequirement: text("light_requirement").notNull(), // low, medium, bright, direct
  minHumidity: integer("min_humidity").notNull().default(40), // percentage
  maxHumidity: integer("max_humidity").notNull().default(80),
  minTemperatureF: integer("min_temperature_f").notNull().default(60),
  maxTemperatureF: integer("max_temperature_f").notNull().default(80),
  wateringFrequency: text("watering_frequency"), // e.g., "weekly", "when soil dry"
  // Growth characteristics
  growthRate: text("growth_rate").notNull().default("moderate"), // slow, moderate, fast
  matureHeightInches: integer("mature_height_inches"),
  matureSpreadInches: integer("mature_spread_inches"),
  // Propagation
  propagationMethods: text("propagation_methods").array(), // division, cutting, spore, seed, offset
  propagationNotes: text("propagation_notes"),
  // Terrarium suitability
  suitableForClosedTerrarium: boolean("suitable_for_closed_terrarium").default(true),
  suitableForOpenTerrarium: boolean("suitable_for_open_terrarium").default(true),
  recommendedBiomes: text("recommended_biomes").array(), // forest, desert, tropical
  // Additional info
  careNotes: text("care_notes"),
  description: text("description"),
  imageUrl: text("image_url"),
  // Stock management
  minStockLevel: integer("min_stock_level").notNull().default(5), // Low stock alert threshold
  targetStockLevel: integer("target_stock_level").notNull().default(20),
  isActive: boolean("is_active").default(true),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("plant_species_common_name_idx").on(table.commonName),
  index("plant_species_scientific_name_idx").on(table.scientificName),
  index("plant_species_light_idx").on(table.lightRequirement),
]);

export const insertPlantSpeciesSchema = createInsertSchema(plantSpecies).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  scientificName: z.string().min(3, "Scientific name must be at least 3 characters"),
  commonName: z.string().min(2, "Common name must be at least 2 characters"),
  family: z.string().optional(),
  lightRequirement: z.enum(lightRequirements),
  minHumidity: z.number().int().min(0).max(100).default(40),
  maxHumidity: z.number().int().min(0).max(100).default(80),
  minTemperatureF: z.number().int().min(32).max(120).default(60),
  maxTemperatureF: z.number().int().min(32).max(120).default(80),
  wateringFrequency: z.string().optional(),
  growthRate: z.enum(growthRates).default("moderate"),
  matureHeightInches: z.number().int().positive().optional(),
  matureSpreadInches: z.number().int().positive().optional(),
  propagationMethods: z.array(z.enum(propagationMethods)).optional(),
  propagationNotes: z.string().optional(),
  suitableForClosedTerrarium: z.boolean().default(true),
  suitableForOpenTerrarium: z.boolean().default(true),
  recommendedBiomes: z.array(z.string()).optional(),
  careNotes: z.string().optional(),
  description: z.string().optional(),
  imageUrl: z.string().url().optional().or(z.literal("")),
  minStockLevel: z.number().int().nonnegative().default(5),
  targetStockLevel: z.number().int().nonnegative().default(20),
  isActive: z.boolean().default(true),
});

export type PlantSpecies = typeof plantSpecies.$inferSelect;
export type InsertPlantSpecies = z.infer<typeof insertPlantSpeciesSchema>;

// Plant Batches - Track individual batches of plants from nurseries
export const quarantineStatuses = ["in_quarantine", "cleared", "rejected"] as const;
export type QuarantineStatus = typeof quarantineStatuses[number];

export const healthGrades = ["A", "B", "C", "D", "F"] as const;
export type HealthGrade = typeof healthGrades[number];

export const growthStages = ["seedling", "juvenile", "young", "mature", "specimen"] as const;
export type GrowthStage = typeof growthStages[number];

export const batchStatuses = ["available", "reserved", "depleted", "disposed"] as const;
export type BatchStatus = typeof batchStatuses[number];

export const plantBatches = pgTable("plant_batches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  speciesId: varchar("species_id").notNull().references(() => plantSpecies.id),
  // Batch tracking
  batchCode: text("batch_code").notNull().unique(), // e.g., "FERN-2024-001"
  initialQuantity: integer("initial_quantity").notNull(),
  currentQuantity: integer("current_quantity").notNull(),
  // Source info
  sourceNursery: text("source_nursery").notNull(),
  nurseryContact: text("nursery_contact"),
  purchasePrice: integer("purchase_price"), // in cents per plant
  acquisitionDate: date("acquisition_date", { mode: "date" }).notNull(),
  // Quarantine & health
  quarantineStatus: text("quarantine_status").notNull().default("in_quarantine"),
  quarantineStartDate: date("quarantine_start_date", { mode: "date" }),
  quarantineEndDate: date("quarantine_end_date", { mode: "date" }),
  quarantineNotes: text("quarantine_notes"),
  healthGrade: text("health_grade").notNull().default("B"), // A, B, C, D, F
  healthNotes: text("health_notes"),
  lastHealthCheck: date("last_health_check", { mode: "date" }),
  // Location & growth
  location: text("location"), // e.g., "Greenhouse A - Shelf 3"
  growthStage: text("growth_stage").notNull().default("young"), // seedling, juvenile, young, mature
  // Status
  status: text("status").notNull().default("available"), // available, reserved, depleted, disposed
  disposalReason: text("disposal_reason"),
  disposalDate: date("disposal_date", { mode: "date" }),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("plant_batches_species_idx").on(table.speciesId),
  index("plant_batches_status_idx").on(table.status),
  index("plant_batches_quarantine_idx").on(table.quarantineStatus),
  index("plant_batches_health_idx").on(table.healthGrade),
  index("plant_batches_acquisition_idx").on(table.acquisitionDate),
]);

export const insertPlantBatchSchema = createInsertSchema(plantBatches).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  speciesId: z.string().uuid("Invalid species ID"),
  batchCode: z.string().min(3, "Batch code must be at least 3 characters"),
  initialQuantity: z.number().int().positive("Initial quantity must be positive"),
  currentQuantity: z.number().int().nonnegative("Current quantity cannot be negative"),
  sourceNursery: z.string().min(2, "Nursery name must be at least 2 characters"),
  nurseryContact: z.string().optional(),
  purchasePrice: z.number().int().nonnegative().optional(),
  acquisitionDate: z.date(),
  quarantineStatus: z.enum(quarantineStatuses).default("in_quarantine"),
  quarantineStartDate: z.date().optional(),
  quarantineEndDate: z.date().optional(),
  quarantineNotes: z.string().optional(),
  healthGrade: z.enum(healthGrades).default("B"),
  healthNotes: z.string().optional(),
  lastHealthCheck: z.date().optional(),
  location: z.string().optional(),
  growthStage: z.enum(growthStages).default("young"),
  status: z.enum(batchStatuses).default("available"),
  disposalReason: z.string().optional(),
  disposalDate: z.date().optional(),
});

export type PlantBatch = typeof plantBatches.$inferSelect;
export type InsertPlantBatch = z.infer<typeof insertPlantBatchSchema>;

// Plant Allocations - Link plants to orders
export const allocationStatuses = ["allocated", "used", "returned", "lost"] as const;
export type AllocationStatus = typeof allocationStatuses[number];

export const plantAllocations = pgTable("plant_allocations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  batchId: varchar("batch_id").notNull().references(() => plantBatches.id),
  orderId: varchar("order_id").notNull().references(() => orders.id),
  quantity: integer("quantity").notNull().default(1),
  status: text("status").notNull().default("allocated"), // allocated, used, returned, lost
  notes: text("notes"),
  allocatedAt: timestamp("allocated_at").defaultNow().notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("plant_allocations_batch_idx").on(table.batchId),
  index("plant_allocations_order_idx").on(table.orderId),
  index("plant_allocations_status_idx").on(table.status),
]);

export const insertPlantAllocationSchema = createInsertSchema(plantAllocations).omit({
  id: true,
  createdAt: true,
  allocatedAt: true,
}).extend({
  batchId: z.string().uuid("Invalid batch ID"),
  orderId: z.string().uuid("Invalid order ID"),
  quantity: z.number().int().positive("Quantity must be positive").default(1),
  status: z.enum(allocationStatuses).default("allocated"),
  notes: z.string().optional(),
  usedAt: z.date().optional(),
});

export type PlantAllocation = typeof plantAllocations.$inferSelect;
export type InsertPlantAllocation = z.infer<typeof insertPlantAllocationSchema>;

// Plant Health Logs - Track health checks and issues over time
export const healthLogTypes = ["routine_check", "issue_found", "treatment", "recovery", "death"] as const;
export type HealthLogType = typeof healthLogTypes[number];

export const plantHealthLogs = pgTable("plant_health_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  batchId: varchar("batch_id").notNull().references(() => plantBatches.id),
  logType: text("log_type").notNull(), // routine_check, issue_found, treatment, recovery, death
  previousHealthGrade: text("previous_health_grade"),
  newHealthGrade: text("new_health_grade"),
  quantityAffected: integer("quantity_affected").default(0),
  quantityLost: integer("quantity_lost").default(0),
  issueDescription: text("issue_description"),
  treatmentApplied: text("treatment_applied"),
  notes: text("notes"),
  loggedBy: text("logged_by"),
  loggedAt: timestamp("logged_at").defaultNow().notNull(),
}, (table) => [
  index("plant_health_logs_batch_idx").on(table.batchId),
  index("plant_health_logs_type_idx").on(table.logType),
  index("plant_health_logs_date_idx").on(table.loggedAt),
]);

export const insertPlantHealthLogSchema = createInsertSchema(plantHealthLogs).omit({
  id: true,
  loggedAt: true,
}).extend({
  batchId: z.string().uuid("Invalid batch ID"),
  logType: z.enum(healthLogTypes),
  previousHealthGrade: z.enum(healthGrades).optional(),
  newHealthGrade: z.enum(healthGrades).optional(),
  quantityAffected: z.number().int().nonnegative().default(0),
  quantityLost: z.number().int().nonnegative().default(0),
  issueDescription: z.string().optional(),
  treatmentApplied: z.string().optional(),
  notes: z.string().optional(),
  loggedBy: z.string().optional(),
});

export type PlantHealthLog = typeof plantHealthLogs.$inferSelect;
export type InsertPlantHealthLog = z.infer<typeof insertPlantHealthLogSchema>;

// Plant Inventory Dashboard Types - for API responses
export interface PlantInventoryStats {
  totalSpecies: number;
  activeSpecies: number;
  totalBatches: number;
  availableBatches: number;
  totalPlants: number;
  availablePlants: number;
  plantsInQuarantine: number;
  lowStockSpecies: { species: PlantSpecies; currentStock: number }[];
  healthDistribution: { grade: string; count: number }[];
  mortalityRate: number; // percentage over last 30 days
  speciesPopulation: { speciesId: string; commonName: string; count: number }[];
  recentHealthIssues: PlantHealthLog[];
  growthStageDistribution: { stage: string; count: number }[];
}

export interface PlantStockAlert {
  speciesId: string;
  species: PlantSpecies;
  currentStock: number;
  minStockLevel: number;
  targetStockLevel: number;
  percentageOfTarget: number;
}

// ============================================================================
// PAYMENT FAILURES TABLE - Tracks failed payments for retry and dunning (Wave 26)
// ============================================================================

export const paymentFailureStatuses = ["pending_retry", "retrying", "recovered", "exhausted", "cancelled"] as const;
export type PaymentFailureStatus = typeof paymentFailureStatuses[number];

export const dunningStages = ["initial", "reminder_1", "reminder_2", "final_warning", "cancelled"] as const;
export type DunningStage = typeof dunningStages[number];

export const paymentFailures = pgTable("payment_failures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id),
  subscriptionId: varchar("subscription_id").references(() => subscriptions.id),
  stripeSubscriptionId: text("stripe_subscription_id"),
  stripeInvoiceId: text("stripe_invoice_id"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  // Failure details
  failureCode: text("failure_code"), // Stripe error code (e.g., "card_declined")
  failureMessage: text("failure_message"),
  failureType: text("failure_type"), // "card_error", "authentication_required", etc.
  amount: integer("amount").notNull(), // Amount in cents
  currency: text("currency").notNull().default("usd"),
  // Retry tracking
  status: text("status").notNull().default("pending_retry"),
  retryCount: integer("retry_count").notNull().default(0),
  maxRetries: integer("max_retries").notNull().default(3), // Configurable per failure
  nextRetryAt: timestamp("next_retry_at"),
  lastRetryAt: timestamp("last_retry_at"),
  recoveredAt: timestamp("recovered_at"),
  // Dunning tracking
  dunningStage: text("dunning_stage").notNull().default("initial"),
  dunningEmailsSent: integer("dunning_emails_sent").notNull().default(0),
  lastDunningEmailAt: timestamp("last_dunning_email_at"),
  // SMS opt-in for critical notifications
  smsOptIn: boolean("sms_opt_in").default(false),
  smsSent: boolean("sms_sent").default(false),
  // Timestamps
  failedAt: timestamp("failed_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("payment_failures_customer_id_idx").on(table.customerId),
  index("payment_failures_subscription_id_idx").on(table.subscriptionId),
  index("payment_failures_status_idx").on(table.status),
  index("payment_failures_next_retry_at_idx").on(table.nextRetryAt),
]);

export const insertPaymentFailureSchema = createInsertSchema(paymentFailures).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  customerId: z.string().uuid("Invalid customer ID"),
  subscriptionId: z.string().uuid("Invalid subscription ID").optional(),
  amount: z.number().int().positive("Amount must be positive"),
  status: z.enum(paymentFailureStatuses).default("pending_retry"),
  dunningStage: z.enum(dunningStages).default("initial"),
});

export type PaymentFailure = typeof paymentFailures.$inferSelect;
export type InsertPaymentFailure = z.infer<typeof insertPaymentFailureSchema>;

// ============================================================================
// PAYMENT RETRY LOG TABLE - Audit trail for all retry attempts
// ============================================================================

export const retryOutcomes = ["success", "failed", "pending", "skipped"] as const;
export type RetryOutcome = typeof retryOutcomes[number];

export const paymentRetryLogs = pgTable("payment_retry_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  paymentFailureId: varchar("payment_failure_id").notNull().references(() => paymentFailures.id),
  retryNumber: integer("retry_number").notNull(),
  outcome: text("outcome").notNull(), // success, failed, pending, skipped
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  // Response details
  responseCode: text("response_code"),
  responseMessage: text("response_message"),
  // Timing
  scheduledFor: timestamp("scheduled_for").notNull(),
  attemptedAt: timestamp("attempted_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("payment_retry_logs_failure_id_idx").on(table.paymentFailureId),
  index("payment_retry_logs_outcome_idx").on(table.outcome),
]);

export const insertPaymentRetryLogSchema = createInsertSchema(paymentRetryLogs).omit({
  id: true,
  createdAt: true,
}).extend({
  paymentFailureId: z.string().uuid("Invalid payment failure ID"),
  retryNumber: z.number().int().positive(),
  outcome: z.enum(retryOutcomes),
  scheduledFor: z.date(),
});

export type PaymentRetryLog = typeof paymentRetryLogs.$inferSelect;
export type InsertPaymentRetryLog = z.infer<typeof insertPaymentRetryLogSchema>;

// ============================================================================
// CARD EXPIRATION REMINDERS TABLE - Proactive card update reminders
// ============================================================================

export const cardExpirationReminders = pgTable("card_expiration_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id),
  stripeCustomerId: text("stripe_customer_id").notNull(),
  stripePaymentMethodId: text("stripe_payment_method_id").notNull(),
  // Card details (for reference - masked)
  cardLast4: text("card_last4").notNull(),
  cardBrand: text("card_brand"), // visa, mastercard, amex, etc.
  expirationMonth: integer("expiration_month").notNull(),
  expirationYear: integer("expiration_year").notNull(),
  // Reminder tracking
  daysBefore: integer("days_before").notNull(), // 30, 14, or 7 days before
  reminderSent: boolean("reminder_sent").default(false),
  sentAt: timestamp("sent_at"),
  cardUpdated: boolean("card_updated").default(false),
  updatedAt: timestamp("updated_at"),
  // Scheduled date
  scheduledFor: timestamp("scheduled_for").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("card_expiration_reminders_customer_id_idx").on(table.customerId),
  index("card_expiration_reminders_scheduled_for_idx").on(table.scheduledFor),
  uniqueIndex("card_expiration_reminders_unique_idx").on(table.customerId, table.stripePaymentMethodId, table.daysBefore),
]);

export const insertCardExpirationReminderSchema = createInsertSchema(cardExpirationReminders).omit({
  id: true,
  createdAt: true,
}).extend({
  customerId: z.string().uuid("Invalid customer ID"),
  stripeCustomerId: z.string().min(1, "Stripe customer ID is required"),
  stripePaymentMethodId: z.string().min(1, "Stripe payment method ID is required"),
  cardLast4: z.string().length(4, "Card last 4 must be exactly 4 digits"),
  expirationMonth: z.number().int().min(1).max(12),
  expirationYear: z.number().int().min(2024).max(2099),
  daysBefore: z.number().int().positive(),
  scheduledFor: z.date(),
});

export type CardExpirationReminder = typeof cardExpirationReminders.$inferSelect;
export type InsertCardExpirationReminder = z.infer<typeof insertCardExpirationReminderSchema>;

// ============================================================================
// DUNNING EMAILS TABLE - Track dunning email sequences
// ============================================================================

export const dunningEmailTypes = ["payment_failed", "reminder_1", "reminder_2", "final_warning", "recovered", "cancelled"] as const;
export type DunningEmailType = typeof dunningEmailTypes[number];

export const dunningEmails = pgTable("dunning_emails", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  paymentFailureId: varchar("payment_failure_id").notNull().references(() => paymentFailures.id),
  customerId: varchar("customer_id").notNull().references(() => customers.id),
  emailType: text("email_type").notNull(), // payment_failed, reminder_1, reminder_2, final_warning
  subject: text("subject").notNull(),
  // Status
  sent: boolean("sent").default(false),
  sentAt: timestamp("sent_at"),
  opened: boolean("opened").default(false),
  openedAt: timestamp("opened_at"),
  clicked: boolean("clicked").default(false),
  clickedAt: timestamp("clicked_at"),
  // Scheduling
  scheduledFor: timestamp("scheduled_for").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("dunning_emails_payment_failure_id_idx").on(table.paymentFailureId),
  index("dunning_emails_customer_id_idx").on(table.customerId),
  index("dunning_emails_scheduled_for_idx").on(table.scheduledFor),
]);

export const insertDunningEmailSchema = createInsertSchema(dunningEmails).omit({
  id: true,
  createdAt: true,
}).extend({
  paymentFailureId: z.string().uuid("Invalid payment failure ID"),
  customerId: z.string().uuid("Invalid customer ID"),
  emailType: z.enum(dunningEmailTypes),
  subject: z.string().min(1, "Subject is required"),
  scheduledFor: z.date(),
});

export type DunningEmail = typeof dunningEmails.$inferSelect;
export type InsertDunningEmail = z.infer<typeof insertDunningEmailSchema>;

// ============================================================================
// PAYMENT METHOD UPDATES TABLE - Self-service payment updates
// ============================================================================

export const paymentMethodUpdateStatuses = ["pending", "completed", "expired", "cancelled"] as const;
export type PaymentMethodUpdateStatus = typeof paymentMethodUpdateStatuses[number];

export const paymentMethodUpdates = pgTable("payment_method_updates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id),
  paymentFailureId: varchar("payment_failure_id").references(() => paymentFailures.id),
  // Secure token for self-service link
  token: text("token").notNull().unique(),
  tokenExpiresAt: timestamp("token_expires_at").notNull(),
  // Stripe checkout session for updating payment method
  stripeSessionId: text("stripe_session_id"),
  // Status tracking
  status: text("status").notNull().default("pending"),
  // Old payment method (for audit)
  oldPaymentMethodId: text("old_payment_method_id"),
  oldCardLast4: text("old_card_last4"),
  // New payment method (after update)
  newPaymentMethodId: text("new_payment_method_id"),
  newCardLast4: text("new_card_last4"),
  // Timestamps
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("payment_method_updates_customer_id_idx").on(table.customerId),
  index("payment_method_updates_token_idx").on(table.token),
  index("payment_method_updates_status_idx").on(table.status),
]);

export const insertPaymentMethodUpdateSchema = createInsertSchema(paymentMethodUpdates).omit({
  id: true,
  createdAt: true,
}).extend({
  customerId: z.string().uuid("Invalid customer ID"),
  paymentFailureId: z.string().uuid("Invalid payment failure ID").optional(),
  token: z.string().min(32, "Token must be at least 32 characters"),
  tokenExpiresAt: z.date(),
  status: z.enum(paymentMethodUpdateStatuses).default("pending"),
});

export type PaymentMethodUpdate = typeof paymentMethodUpdates.$inferSelect;
export type InsertPaymentMethodUpdate = z.infer<typeof insertPaymentMethodUpdateSchema>;

// ============================================================================
// PAYMENT RECOVERY METRICS - Dashboard analytics
// ============================================================================

export interface PaymentRecoveryMetrics {
  totalFailures: number;
  pendingRetries: number;
  recovered: number;
  exhausted: number;
  recoveryRate: number; // percentage
  averageRecoveryTime: number; // in hours
  byRetryNumber: {
    retry1: { attempted: number; recovered: number };
    retry2: { attempted: number; recovered: number };
    retry3: { attempted: number; recovered: number };
  };
  byFailureType: Record<string, { count: number; recoveryRate: number }>;
  revenueAtRisk: number; // Total amount pending recovery
  revenueRecovered: number; // Total amount recovered
}

// Retry schedule configuration (in days after failure)
export const DEFAULT_RETRY_SCHEDULE = [1, 3, 7] as const; // Day 1, Day 3, Day 7

// Card expiration reminder schedule (days before expiration)
export const CARD_EXPIRATION_REMINDER_DAYS = [30, 14, 7] as const;

// Dunning email schedule (days after failure)
export const DUNNING_EMAIL_SCHEDULE = {
  payment_failed: 0,    // Immediately
  reminder_1: 3,        // 3 days after failure
  reminder_2: 7,        // 7 days after failure
  final_warning: 10,    // 10 days after failure
} as const;

// ============================================================================
// COMMUNITY MODERATION SYSTEM (Wave 24)
// Comprehensive moderation infrastructure for community governance
// ============================================================================

// Report Categories - Types of content that can be reported
export const reportCategories = [
  "hate_speech",
  "harassment",
  "spam",
  "dangerous_advice",
  "intellectual_property",
  "inappropriate_content",
  "privacy_violation",
  "self_promotion",
  "misinformation",
  "other"
] as const;
export type ReportCategory = typeof reportCategories[number];

// Report Statuses - Lifecycle of a report
export const reportStatuses = ["pending", "under_review", "resolved", "dismissed"] as const;
export type ReportStatus = typeof reportStatuses[number];

// Moderation Actions - Actions moderators can take
export const moderationActions = [
  "warning",
  "content_removal",
  "temporary_restriction",
  "temporary_ban",
  "permanent_ban",
  "appeal_granted",
  "no_action"
] as const;
export type ModerationAction = typeof moderationActions[number];

// Ban Types
export const banTypes = ["temporary", "permanent"] as const;
export type BanType = typeof banTypes[number];

// Content Types - What is being reported
export const contentTypes = ["post", "comment", "photo", "profile", "message"] as const;
export type ContentType = typeof contentTypes[number];

// Appeal Statuses
export const appealStatuses = ["pending", "under_review", "approved", "denied"] as const;
export type AppealStatus = typeof appealStatuses[number];

// Community Reports Table - User-submitted reports of problematic content
export const communityReports = pgTable("community_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Reporter info
  reporterEmail: text("reporter_email").notNull(),
  reporterName: text("reporter_name"),
  reporterCustomerId: varchar("reporter_customer_id").references(() => customers.id),
  // Reported content
  contentType: text("content_type").notNull(),
  contentId: varchar("content_id"),
  contentUrl: text("content_url"),
  contentSnapshot: text("content_snapshot"),
  // Reported user (if applicable)
  reportedUserEmail: text("reported_user_email"),
  reportedUserName: text("reported_user_name"),
  reportedCustomerId: varchar("reported_customer_id").references(() => customers.id),
  // Report details
  category: text("category").notNull(),
  subcategory: text("subcategory"),
  description: text("description").notNull(),
  additionalContext: text("additional_context"),
  // Status & resolution
  status: text("status").notNull().default("pending"),
  priority: text("priority").default("normal"),
  assignedModerator: text("assigned_moderator"),
  // Resolution details
  resolution: text("resolution"),
  resolutionNotes: text("resolution_notes"),
  actionTaken: text("action_taken"),
  resolvedBy: text("resolved_by"),
  resolvedAt: timestamp("resolved_at"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("community_reports_status_idx").on(table.status),
  index("community_reports_category_idx").on(table.category),
  index("community_reports_priority_idx").on(table.priority),
  index("community_reports_reporter_idx").on(table.reporterEmail),
  index("community_reports_reported_idx").on(table.reportedUserEmail),
  index("community_reports_created_idx").on(table.createdAt),
]);

export const insertCommunityReportSchema = createInsertSchema(communityReports).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  resolvedAt: true,
}).extend({
  reporterEmail: z.string().email("Please enter a valid email address"),
  reporterName: z.string().optional(),
  reporterCustomerId: z.string().uuid("Invalid customer ID").optional(),
  contentType: z.enum(contentTypes),
  contentId: z.string().optional(),
  contentUrl: z.string().url().optional().or(z.literal("")),
  contentSnapshot: z.string().optional(),
  reportedUserEmail: z.string().email().optional().or(z.literal("")),
  reportedUserName: z.string().optional(),
  reportedCustomerId: z.string().uuid("Invalid customer ID").optional(),
  category: z.enum(reportCategories),
  subcategory: z.string().optional(),
  description: z.string().min(10, "Please provide more detail about the issue"),
  additionalContext: z.string().optional(),
  status: z.enum(reportStatuses).default("pending"),
  priority: z.enum(["low", "normal", "high", "critical"]).default("normal"),
  assignedModerator: z.string().email().optional(),
  resolution: z.string().optional(),
  resolutionNotes: z.string().optional(),
  actionTaken: z.enum(moderationActions).optional(),
  resolvedBy: z.string().email().optional(),
});

export type CommunityReport = typeof communityReports.$inferSelect;
export type InsertCommunityReport = z.infer<typeof insertCommunityReportSchema>;

// Moderation Actions Log - Audit trail of all moderation actions
export const moderationActionsLog = pgTable("moderation_actions_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  targetEmail: text("target_email").notNull(),
  targetName: text("target_name"),
  targetCustomerId: varchar("target_customer_id").references(() => customers.id),
  reportId: varchar("report_id").references(() => communityReports.id),
  actionType: text("action_type").notNull(),
  severity: integer("severity").notNull().default(1),
  reason: text("reason").notNull(),
  internalNotes: text("internal_notes"),
  publicNotes: text("public_notes"),
  durationDays: integer("duration_days"),
  expiresAt: timestamp("expires_at"),
  contentType: text("content_type"),
  contentId: varchar("content_id"),
  contentRemoved: boolean("content_removed").default(false),
  moderatorEmail: text("moderator_email").notNull(),
  moderatorName: text("moderator_name"),
  appealable: boolean("appealable").default(true),
  appealed: boolean("appealed").default(false),
  appealId: varchar("appeal_id"),
  isActive: boolean("is_active").default(true),
  revokedAt: timestamp("revoked_at"),
  revokedBy: text("revoked_by"),
  revokeReason: text("revoke_reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("moderation_actions_target_idx").on(table.targetEmail),
  index("moderation_actions_type_idx").on(table.actionType),
  index("moderation_actions_report_idx").on(table.reportId),
  index("moderation_actions_moderator_idx").on(table.moderatorEmail),
  index("moderation_actions_active_idx").on(table.isActive),
  index("moderation_actions_expires_idx").on(table.expiresAt),
]);

export const insertModerationActionSchema = createInsertSchema(moderationActionsLog).omit({
  id: true,
  createdAt: true,
}).extend({
  targetEmail: z.string().email("Please enter a valid email address"),
  targetName: z.string().optional(),
  targetCustomerId: z.string().uuid("Invalid customer ID").optional(),
  reportId: z.string().uuid("Invalid report ID").optional(),
  actionType: z.enum(moderationActions),
  severity: z.number().int().min(1).max(5).default(1),
  reason: z.string().min(5, "Please provide a reason for this action"),
  internalNotes: z.string().optional(),
  publicNotes: z.string().optional(),
  durationDays: z.number().int().positive().optional(),
  expiresAt: z.date().optional(),
  contentType: z.enum(contentTypes).optional(),
  contentId: z.string().optional(),
  contentRemoved: z.boolean().default(false),
  moderatorEmail: z.string().email("Please enter a valid moderator email"),
  moderatorName: z.string().optional(),
  appealable: z.boolean().default(true),
  appealed: z.boolean().default(false),
  appealId: z.string().uuid().optional(),
  isActive: z.boolean().default(true),
  revokedAt: z.date().optional(),
  revokedBy: z.string().email().optional(),
  revokeReason: z.string().optional(),
});

export type ModerationActionLog = typeof moderationActionsLog.$inferSelect;
export type InsertModerationAction = z.infer<typeof insertModerationActionSchema>;

// User Bans Table
export const userBans = pgTable("user_bans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userEmail: text("user_email").notNull(),
  userName: text("user_name"),
  customerId: varchar("customer_id").references(() => customers.id),
  banType: text("ban_type").notNull(),
  reason: text("reason").notNull(),
  publicReason: text("public_reason"),
  moderationActionId: varchar("moderation_action_id").references(() => moderationActionsLog.id),
  startDate: timestamp("start_date").defaultNow().notNull(),
  endDate: timestamp("end_date"),
  isActive: boolean("is_active").default(true),
  liftedAt: timestamp("lifted_at"),
  liftedBy: text("lifted_by"),
  liftReason: text("lift_reason"),
  appealEligible: boolean("appeal_eligible").default(true),
  appealSubmitted: boolean("appeal_submitted").default(false),
  bannedBy: text("banned_by").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("user_bans_email_idx").on(table.userEmail),
  index("user_bans_active_idx").on(table.isActive),
  index("user_bans_type_idx").on(table.banType),
  index("user_bans_end_date_idx").on(table.endDate),
]);

export const insertUserBanSchema = createInsertSchema(userBans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userEmail: z.string().email("Please enter a valid email address"),
  userName: z.string().optional(),
  customerId: z.string().uuid("Invalid customer ID").optional(),
  banType: z.enum(banTypes),
  reason: z.string().min(10, "Please provide a detailed reason for the ban"),
  publicReason: z.string().optional(),
  moderationActionId: z.string().uuid("Invalid moderation action ID").optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  isActive: z.boolean().default(true),
  liftedAt: z.date().optional(),
  liftedBy: z.string().email().optional(),
  liftReason: z.string().optional(),
  appealEligible: z.boolean().default(true),
  appealSubmitted: z.boolean().default(false),
  bannedBy: z.string().email("Please enter a valid moderator email"),
});

export type UserBan = typeof userBans.$inferSelect;
export type InsertUserBan = z.infer<typeof insertUserBanSchema>;

// User Warnings Table
export const userWarnings = pgTable("user_warnings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userEmail: text("user_email").notNull(),
  userName: text("user_name"),
  customerId: varchar("customer_id").references(() => customers.id),
  reason: text("reason").notNull(),
  message: text("message").notNull(),
  guidelineViolated: text("guideline_violated"),
  reportId: varchar("report_id").references(() => communityReports.id),
  moderationActionId: varchar("moderation_action_id").references(() => moderationActionsLog.id),
  contentType: text("content_type"),
  contentId: varchar("content_id"),
  acknowledged: boolean("acknowledged").default(false),
  acknowledgedAt: timestamp("acknowledged_at"),
  issuedBy: text("issued_by").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("user_warnings_email_idx").on(table.userEmail),
  index("user_warnings_created_idx").on(table.createdAt),
]);

export const insertUserWarningSchema = createInsertSchema(userWarnings).omit({
  id: true,
  createdAt: true,
}).extend({
  userEmail: z.string().email("Please enter a valid email address"),
  userName: z.string().optional(),
  customerId: z.string().uuid("Invalid customer ID").optional(),
  reason: z.string().min(5, "Please provide a reason for the warning"),
  message: z.string().min(10, "Please provide a message for the user"),
  guidelineViolated: z.string().optional(),
  reportId: z.string().uuid("Invalid report ID").optional(),
  moderationActionId: z.string().uuid("Invalid moderation action ID").optional(),
  contentType: z.enum(contentTypes).optional(),
  contentId: z.string().optional(),
  acknowledged: z.boolean().default(false),
  acknowledgedAt: z.date().optional(),
  issuedBy: z.string().email("Please enter a valid moderator email"),
});

export type UserWarning = typeof userWarnings.$inferSelect;
export type InsertUserWarning = z.infer<typeof insertUserWarningSchema>;

// Appeals Table
export const moderationAppeals = pgTable("moderation_appeals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  appellantEmail: text("appellant_email").notNull(),
  appellantName: text("appellant_name"),
  appellantCustomerId: varchar("appellant_customer_id").references(() => customers.id),
  moderationActionId: varchar("moderation_action_id").references(() => moderationActionsLog.id),
  banId: varchar("ban_id").references(() => userBans.id),
  appealReason: text("appeal_reason").notNull(),
  additionalContext: text("additional_context"),
  evidence: text("evidence"),
  status: text("status").notNull().default("pending"),
  reviewedBy: text("reviewed_by"),
  reviewNotes: text("review_notes"),
  decision: text("decision"),
  decisionReason: text("decision_reason"),
  reviewedAt: timestamp("reviewed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("moderation_appeals_email_idx").on(table.appellantEmail),
  index("moderation_appeals_status_idx").on(table.status),
  index("moderation_appeals_action_idx").on(table.moderationActionId),
  index("moderation_appeals_ban_idx").on(table.banId),
]);

export const insertModerationAppealSchema = createInsertSchema(moderationAppeals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  reviewedAt: true,
}).extend({
  appellantEmail: z.string().email("Please enter a valid email address"),
  appellantName: z.string().optional(),
  appellantCustomerId: z.string().uuid("Invalid customer ID").optional(),
  moderationActionId: z.string().uuid("Invalid moderation action ID").optional(),
  banId: z.string().uuid("Invalid ban ID").optional(),
  appealReason: z.string().min(20, "Please provide a detailed explanation of your appeal"),
  additionalContext: z.string().optional(),
  evidence: z.string().optional(),
  status: z.enum(appealStatuses).default("pending"),
  reviewedBy: z.string().email().optional(),
  reviewNotes: z.string().optional(),
  decision: z.enum(["approved", "denied"]).optional(),
  decisionReason: z.string().optional(),
});

export type ModerationAppeal = typeof moderationAppeals.$inferSelect;
export type InsertModerationAppeal = z.infer<typeof insertModerationAppealSchema>;

// Moderation Dashboard Stats Interface
export interface ModerationStats {
  pendingReports: number;
  reportsToday: number;
  reportsThisWeek: number;
  reportsThisMonth: number;
  activeBans: number;
  temporaryBans: number;
  permanentBans: number;
  pendingAppeals: number;
  warningsThisMonth: number;
  reportsByCategory: { category: ReportCategory; count: number }[];
  actionsByType: { actionType: ModerationAction; count: number }[];
  averageResolutionTime: number;
  moderatorActivity: { email: string; actionsCount: number }[];
}

// Report with related data for admin views
export interface CommunityReportWithDetails extends CommunityReport {
  previousActions: ModerationActionLog[];
  userWarningCount: number;
  userBanHistory: UserBan[];
}

// Category display names for UI
export const reportCategoryDisplayNames: Record<ReportCategory, string> = {
  hate_speech: "Hate Speech",
  harassment: "Harassment & Bullying",
  spam: "Spam",
  dangerous_advice: "Dangerous Plant Advice",
  intellectual_property: "Intellectual Property Violation",
  inappropriate_content: "Inappropriate Content",
  privacy_violation: "Privacy Violation",
  self_promotion: "Excessive Self-Promotion",
  misinformation: "Misinformation",
  other: "Other",
};

// Action display names for UI
export const moderationActionDisplayNames: Record<ModerationAction, string> = {
  warning: "Warning Issued",
  content_removal: "Content Removed",
  temporary_restriction: "Temporary Restriction",
  temporary_ban: "Temporary Ban",
  permanent_ban: "Permanent Ban",
  appeal_granted: "Appeal Granted",
  no_action: "No Action Taken",
};
